import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, 
  RefreshCw,
  Download,
  Target,
  Frame,
  Heart,
  Lightbulb,
  Info,
  Brain,
  CheckCircle2,
  Calendar,
  Users,
  Sparkles,
  FileText,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";

const SectionCard = ({ title, icon: Icon, iconColor, children }) => (
  <Card className="p-5 bg-card border-border shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", iconColor)}>
        <Icon className="w-4 h-4" />
      </div>
      <h3 className="font-semibold text-foreground">{title}</h3>
    </div>
    {children}
  </Card>
);

const TagList = ({ items, emptyText = "None specified" }) => {
  if (!items || items.length === 0) {
    return <span className="text-sm text-muted-foreground italic">{emptyText}</span>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, index) => (
        <Badge key={index} variant="secondary" className="text-xs">
          {typeof item === 'object' ? item.text : item}
        </Badge>
      ))}
    </div>
  );
};

const RatingBar = ({ label, value, color }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-muted-foreground w-20 shrink-0">{label}</span>
    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
      <div 
        className={cn("h-full rounded-full transition-all", color)}
        style={{ width: `${value}%` }}
      />
    </div>
    <span className="text-xs font-medium text-foreground w-10 text-right">{value}%</span>
  </div>
);

const DecisionSummary = ({ decisionData, onBack, onStartOver }) => {
  const summaryRef = useRef(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const { ratings } = decisionData;
  const weakestLink = Math.min(...Object.values(ratings));
  const averageRating = Math.round(Object.values(ratings).reduce((a, b) => a + b, 0) / 6);

  const handleDownloadPDF = async () => {
    if (!summaryRef.current) return;
    
    setIsGenerating(true);
    toast.info("Generating PDF...");

    try {
      const element = summaryRef.current;
      
      // Create canvas from the summary content
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `conversations-for-clarity-${date}.pdf`;
      
      // Download
      pdf.save(filename);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen py-8 px-4 sm:px-6 lg:px-12 print:p-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 print:mb-4"
        >
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-muted-foreground hover:text-foreground print:hidden"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Steps
            </Button>
            
            <div className="flex gap-2 print:hidden">
              <Button 
                variant="outline" 
                onClick={handleDownloadPDF}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {isGenerating ? "Generating..." : "Download PDF"}
              </Button>
              <Button variant="outline" onClick={onStartOver}>
                <RefreshCw className="w-4 h-4 mr-2" />
                New Decision
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                Decision Summary
              </h1>
              <p className="text-sm text-muted-foreground">
                Your Conversations for Clarity worksheet
              </p>
            </div>
          </div>
        </motion.div>

        {/* Summary Content - with ref for PDF generation */}
        <div ref={summaryRef} className="space-y-6 print:space-y-4 bg-background p-4 -m-4">
          {/* Goal & Challenge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <SectionCard title="What I Want to Achieve" icon={Target} iconColor="bg-primary/10 text-primary">
              <p className="text-foreground mb-4">{decisionData.whatToAchieve || "Not specified"}</p>
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Why it's challenging:</p>
                <p className="text-sm text-foreground">{decisionData.whyChallenging || "Not specified"}</p>
              </div>
            </SectionCard>
          </motion.div>

          {/* Frame & Values Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid md:grid-cols-2 gap-4"
          >
            {/* Frame */}
            <SectionCard title="Frame" icon={Frame} iconColor="bg-chain-frame/20 text-chain-frame">
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Decisions I Face</p>
                  <TagList items={decisionData.decisionsIFace} />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Who's Involved</p>
                  <TagList items={decisionData.whoElseInvolved} />
                </div>
                {decisionData.deadline && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>Deadline: {decisionData.deadline}</span>
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Values */}
            <SectionCard title="Values" icon={Heart} iconColor="bg-chain-values/20 text-chain-values">
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">What I Want</p>
                  <TagList items={decisionData.whatIWant} />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">What I Want to Avoid</p>
                  <TagList items={decisionData.whatIWantToAvoid} />
                </div>
                {decisionData.tradeoffs && (
                  <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                    <span className="font-medium">Trade-offs:</span> {decisionData.tradeoffs}
                  </div>
                )}
              </div>
            </SectionCard>
          </motion.div>

          {/* Alternatives & Information Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid md:grid-cols-2 gap-4"
          >
            {/* Alternatives */}
            <SectionCard title="Alternatives" icon={Lightbulb} iconColor="bg-chain-alternatives/20 text-chain-alternatives">
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Options I See</p>
                  <TagList items={decisionData.optionsISee} />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Creative Approaches</p>
                  <TagList items={decisionData.creativeApproaches} />
                </div>
                {decisionData.constraintFreeOptions && (
                  <div className="text-sm text-muted-foreground bg-accent/30 p-2 rounded">
                    <span className="font-medium">If no constraints:</span> {decisionData.constraintFreeOptions}
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Information */}
            <SectionCard title="Information" icon={Info} iconColor="bg-chain-information/20 text-chain-information">
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">What I Need to Know</p>
                  <TagList items={decisionData.whatINeedToKnow} />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Who to Talk To</p>
                  <TagList items={decisionData.whoToTalkTo} />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Uncertainties</p>
                  <TagList items={decisionData.uncertainties} />
                </div>
              </div>
            </SectionCard>
          </motion.div>

          {/* Decision Chain Ratings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <SectionCard title="Decision Chain Strength" icon={Brain} iconColor="bg-chain-reasoning/20 text-chain-reasoning">
              <div className="space-y-3 mb-4">
                <RatingBar label="Frame" value={ratings.helpfulFrame} color="bg-chain-frame" />
                <RatingBar label="Values" value={ratings.clearValues} color="bg-chain-values" />
                <RatingBar label="Alternatives" value={ratings.creativeAlternatives} color="bg-chain-alternatives" />
                <RatingBar label="Information" value={ratings.usefulInformation} color="bg-chain-information" />
                <RatingBar label="Reasoning" value={ratings.soundReasoning} color="bg-chain-reasoning" />
                <RatingBar label="Commitment" value={ratings.commitmentToFollow} color="bg-chain-commitment" />
              </div>
              
              <Separator className="my-4" />
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Weakest Link</p>
                  <p className={cn(
                    "text-xl font-bold",
                    weakestLink < 50 ? "text-warning" : weakestLink < 70 ? "text-accent-warm" : "text-success"
                  )}>
                    {weakestLink}%
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Average Strength</p>
                  <p className="text-xl font-bold text-foreground">{averageRating}%</p>
                </div>
              </div>
            </SectionCard>
          </motion.div>

          {/* Readiness Check */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <SectionCard title="Decision Readiness" icon={CheckCircle2} iconColor="bg-success/20 text-success">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className={cn(
                  "p-3 rounded-lg text-center",
                  decisionData.amIFitToDecide ? "bg-success/10" : "bg-muted"
                )}>
                  <p className="text-xs text-muted-foreground mb-1">Fit to Decide</p>
                  <p className={cn(
                    "font-semibold",
                    decisionData.amIFitToDecide ? "text-success" : "text-muted-foreground"
                  )}>
                    {decisionData.amIFitToDecide === null ? "—" : decisionData.amIFitToDecide ? "Yes" : "No"}
                  </p>
                </div>
                <div className={cn(
                  "p-3 rounded-lg text-center",
                  decisionData.amIReadyToDecide ? "bg-success/10" : "bg-muted"
                )}>
                  <p className="text-xs text-muted-foreground mb-1">Ready to Decide</p>
                  <p className={cn(
                    "font-semibold",
                    decisionData.amIReadyToDecide ? "text-success" : "text-muted-foreground"
                  )}>
                    {decisionData.amIReadyToDecide === null ? "—" : decisionData.amIReadyToDecide ? "Yes" : "No"}
                  </p>
                </div>
                <div className={cn(
                  "p-3 rounded-lg text-center",
                  decisionData.doesItMakeSense ? "bg-success/10" : "bg-muted"
                )}>
                  <p className="text-xs text-muted-foreground mb-1">Makes Sense</p>
                  <p className={cn(
                    "font-semibold",
                    decisionData.doesItMakeSense ? "text-success" : "text-muted-foreground"
                  )}>
                    {decisionData.doesItMakeSense === null ? "—" : decisionData.doesItMakeSense ? "Yes" : "No"}
                  </p>
                </div>
                <div className={cn(
                  "p-3 rounded-lg text-center",
                  decisionData.doesItFeelRight ? "bg-success/10" : "bg-muted"
                )}>
                  <p className="text-xs text-muted-foreground mb-1">Feels Right</p>
                  <p className={cn(
                    "font-semibold",
                    decisionData.doesItFeelRight ? "text-success" : "text-muted-foreground"
                  )}>
                    {decisionData.doesItFeelRight === null ? "—" : decisionData.doesItFeelRight ? "Yes" : "No"}
                  </p>
                </div>
              </div>
            </SectionCard>
          </motion.div>

          {/* Commitment & Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <SectionCard title="My Commitment" icon={Sparkles} iconColor="bg-primary/10 text-primary">
              <div className="space-y-4">
                <div className="bg-primary/5 border border-primary/20 p-4 rounded-xl">
                  <p className="text-lg font-medium text-foreground">
                    {decisionData.commitment || "No commitment written yet"}
                  </p>
                  {decisionData.targetDate && (
                    <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>Target: {new Date(decisionData.targetDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                
                {decisionData.nextSteps.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Next Steps:</p>
                    <ul className="space-y-2">
                      {decisionData.nextSteps.map((step, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className={cn(
                            "w-4 h-4 shrink-0",
                            step.completed ? "text-success" : "text-muted-foreground"
                          )} />
                          <span className={step.completed ? "line-through text-muted-foreground" : "text-foreground"}>
                            {step.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </SectionCard>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center py-6 print:py-2"
          >
            <p className="text-sm text-muted-foreground">
              Based on the Decision Education Foundation's Conversations for Clarity framework
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              decisioneducation.org
            </p>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default DecisionSummary;
